import React, { useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import {
  MDBRow,
  MDBCol,
  MDBInputGroup,
  MDBInput,
  MDBIcon,
} from "mdb-react-ui-kit";
import { getAllCommitteesLegislatorIsAssociatedWith } from "../../../../utils/committeesOperations";
import ExportDropdown from "../../../common/ExportDropdown";
import { exportToCSV, exportToPDF } from "../../../../utils/exportDatatable";

const Committees = ({ legislator }) => {
  const [committees, setCommittees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    committeeName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    committeeType: { value: null, matchMode: FilterMatchMode.CONTAINS },
    positionName: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  useEffect(() => {
    const fetchCommittees = async () => {
      try {
        setLoading(true);
        const committeePromises =
          legislator.legislatorDetails.sessionMemberships.map(
            (sessionMembership) =>
              getAllCommitteesLegislatorIsAssociatedWith({
                legislatorId: legislator.legislatorDetails.id,
                sessionId: sessionMembership.session.id,
              })
          );

        const committeesData = await Promise.all(committeePromises);
        const flattened = committeesData.flat().map((row) => {
          const committee = row.nonStandingCommittee || row.standingCommittee;
          const name =
            committee?.committeeDetails?.committeeCode?.name
              ?.replace(/\(H\)|\(S\)/g, "")
              .trim() || "";
          const type =
            committee?.committeeDetails?.committeeCode?.committeeType
              ?.description || "";
          const position = row.type?.name || "";

          return {
            ...row,
            committeeName: name,
            committeeType: type,
            positionName: position,
          };
        });

        setCommittees(flattened);
      } catch (error) {
        console.error("Error fetching committees:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCommittees();
  }, [legislator]);

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let _filters = { ...filters };
    _filters["global"].value = value;

    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const nameBodyTemplate = (rowData) => {
    const committee = rowData.nonStandingCommittee || rowData.standingCommittee;
    const isNonStanding = !!rowData.nonStandingCommittee;

    return (
      <a
        href={`${process.env.REACT_APP_COMMITTEES_EXPLORER}/#/${
          isNonStanding ? "nonStandingCommittees" : "standingCommittees"
        }/${committee.id}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {committee?.committeeDetails?.committeeCode?.name
          ?.replace(/\(H\)|\(S\)/g, "")
          .trim() || ""}
      </a>
    );
  };

  const exportColumns = [
    { field: "committeeName", header: "Committee Name" },
    { field: "committeeType", header: "Type" },
    { field: "positionName", header: "Position" },
  ];

  const handleExport = (type) => {
    if (type === "csv") {
      exportToCSV(`${legislator?.legislatorDetails?.lastName}-${legislator?.legislatorDetails?.firstName}-Committees`, exportColumns, committees);
    } else if (type === "pdf") {
      exportToPDF(`${legislator?.legislatorDetails?.lastName}-${legislator?.legislatorDetails?.firstName}-Committees`, exportColumns, committees, {});
    }
  };

  return (
    <>
      <MDBRow className="g-1">
        <MDBCol size="lg">
          <MDBInputGroup
            className="mb-3"
            noBorder
            textBefore={<MDBIcon fas icon="search" />}
          >
            <MDBInput
              type="text"
              label="Search Committees"
              value={globalFilterValue}
              onChange={onGlobalFilterChange}
            />
          </MDBInputGroup>
        </MDBCol>
        <MDBCol size="auto">
          <ExportDropdown
            data={committees}
            onExport={handleExport}
            columns={exportColumns}
            fileName={`committees-${legislator?.lastName || "export"}`}
          />
        </MDBCol>
      </MDBRow>
      <DataTable
        value={committees}
        showGridlines
        stripedRows
        resizableColumns
        paginator
        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
        rows={100}
        rowsPerPageOptions={[25, 50, 100]}
        scrollable
        scrollHeight="650px"
        sortField="committeeName"
        sortOrder={1}
        removableSort
        loading={loading}
        filters={filters}
        globalFilterFields={["committeeName", "committeeType", "positionName"]}
        emptyMessage="No committees found."
      >
        <Column
          header="Committee Name"
          field="committeeName"
          body={nameBodyTemplate}
          sortable
          filter
          filterPlaceholder="Search Name"
        />
        <Column
          header="Type"
          field="committeeType"
          sortable
          filter
          filterPlaceholder="Search Type"
        />
        <Column
          header="Position"
          field="positionName"
          sortable
          filter
          filterPlaceholder="Search Position"
        />
      </DataTable>
    </>
  );
};

export default Committees;
